import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import HttpBackend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import { orgWordsPostProcessor, getCurrentOrg, setCurrentOrg, subscribeOrg, ORG_CACHE_KEY } from "./utils/orgWords";

// UI strings live in /public/locales/{{lng}}.json and are maintained by hand today.
// We only ship en.json and fr.json, but the language master can list any number of
// extra languages (de, es, ...). When the user switches to one of those, i18next
// tries to load the matching file — which doesn't exist — and would normally throw.
// We intercept the HTTP request, treat 404 as an empty bundle, and rely on
// `fallbackLng: 'en'` to render English UI labels. Master-data labels keep working
// because they resolve via the localStorage cache, not these files.
const tolerantRequest = (options, url, payload, callback) => {
    fetch(url)
        .then(async (res) => {
            if (res.status === 404) {
                // Silent fallback — no error, empty bundle, English wins via fallbackLng.
                return callback(null, { status: 200, data: '{}' });
            }
            const text = await res.text();
            return callback(null, { status: res.status, data: text });
        })
        .catch((err) => callback(err, null));
};

// ---- "hospital" in the words of the signed-in tenant's kind ------------------
// Owner, 30 Sep 2026: a tenant is a Hospital, a Clinic or MOC, and the words on
// screen follow it. orgWordsPostProcessor (utils/orgWords.js) runs on EVERY
// translation — including the t('key', 'Default text') form, which i18next
// post-processes just the same — so no locale string needs rewriting.

// A reload reads right at once: the org answered last time is cached (and
// wiped with everything else by localStorage.clear() at sign-out).
try {
    if (typeof window !== 'undefined' && window.localStorage
        && window.localStorage.getItem('isAuthenticated') === 'true') {
        const cached = window.localStorage.getItem(ORG_CACHE_KEY);
        if (cached) setCurrentOrg(JSON.parse(cached));
    }
} catch { /* no cache — the words stay "hospital" until the server answers */ }

// When the org arrives or changes, the text already on screen is redrawn:
// every useTranslation() is bound to 'orgChanged' (react.bindI18n below) and
// takes a fresh t — no reload.
subscribeOrg(() => { i18n.emit('orgChanged', getCurrentOrg()); });

if (typeof window !== 'undefined') {
    // Sign-out (authSlice dispatches 'logout' after localStorage.clear()).
    window.addEventListener('logout', () => setCurrentOrg(null));
    // Sign-out in another tab clears the shared storage.
    window.addEventListener('storage', (e) => {
        if (e.key === null || e.key === ORG_CACHE_KEY || (e.key === 'isAuthenticated' && e.newValue !== 'true')) {
            try {
                const cached = window.localStorage.getItem(ORG_CACHE_KEY);
                setCurrentOrg(cached && window.localStorage.getItem('isAuthenticated') === 'true' ? JSON.parse(cached) : null);
            } catch { setCurrentOrg(null); }
        }
    });
}

i18n
    .use(orgWordsPostProcessor)
    .use(HttpBackend) // Load translations from files
    .use(LanguageDetector) // Detect language from browser/localStorage
    .use(initReactI18next) // Pass i18n to react-i18next
    .init({
        // Intentionally NOT restricting supportedLngs: the language master drives the
        // available list at runtime. Any code picked from the header popup is accepted,
        // missing UI-string files fall back to English automatically.
        fallbackLng: "en",
        // Hard default: English. If localStorage already has a saved choice the
        // detector still honours it, but on a fresh install / cleared storage the
        // app boots in English instead of the browser's system language.
        lng: typeof window !== 'undefined' && window.localStorage
            ? (window.localStorage.getItem('i18nextLng') || 'en')
            : 'en',
        load: 'currentOnly',
        debug: false,
        backend: {
            loadPath: "/locales/{{lng}}.json",
            request: tolerantRequest,
        },
        detection: {
            // Only localStorage — never fall through to navigator/browser language.
            // This guarantees a fresh user sees English first; a returning user who
            // previously picked a different language still gets their choice.
            order: ['localStorage'],
            caches: ['localStorage'],
        },
        interpolation: {
            escapeValue: false // React already escapes HTML
        },
        // Every translation passes through the tenant-kind words (see above).
        postProcess: ['orgWords'],
        react: {
            // 'orgChanged' redraws the screen when the tenant's kind arrives.
            bindI18n: 'languageChanged orgChanged',
        },
    });

export default i18n;
